import React, { useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getNextProject, getProjectBySlug } from '../data/projects';
import { MediaCarousel } from './MediaCarousel';
import { SectionLabel } from './SectionLabel';
const WINNOW_IMAGES = {
  cover: 'https://cdn.magicpatterns.com/uploads/nBXRU9DNX9vvoKVPpJCKwu/winnow-cover-3.png',
  originalApp: 'https://cdn.magicpatterns.com/uploads/ktBahffBus9bFFE2CJnFmY/winnow-original-app.png',
  affinityMap: 'https://cdn.magicpatterns.com/uploads/j5FcqzaFRCKAMP8fZhSRiX/winnow-affinity-map.png',
  cardSorting: 'https://cdn.magicpatterns.com/uploads/qJQv5UXyckp7WdnC9MKbDh/winnow-card-sorting.png',
  sitemap: 'https://cdn.magicpatterns.com/uploads/p8MoRmQrvJJtXCBfGxaMd1/winnow-sitemap.png',
  wireImprovements: 'https://cdn.magicpatterns.com/uploads/645S7cXZtieamZKwkd8PRP/wInnow-wire-improvements.png',
  wireHighlights: 'https://cdn.magicpatterns.com/uploads/kWKok2Vnhk5HzaazUQ5V6d/winnow-wire-highlights2.mp4',
  clientFlow: 'https://cdn.magicpatterns.com/uploads/rqVNvoQYQxkXNL4W9T2U2Q/winnow-highlight-client-flow.png',
  clientFlowExample: 'https://cdn.magicpatterns.com/uploads/vU5K2KoV8RcuuBSiKS6huX/winnow-highlight-client-flow-example.png',
  hifiProfile: 'https://cdn.magicpatterns.com/uploads/nymqS8KpWZfPd1wqehJFYu/winnow-hifi1.mp4',
  hifiPostCreation: 'https://cdn.magicpatterns.com/uploads/sSQW6YgXagyxGKrWX8gtg9/winnow-hifi2.mp4',
  hifiAnalytics: 'https://cdn.magicpatterns.com/uploads/oVUXKZu8tmmxYrgv1nHTXG/winnow-hifi3.mp4'
} as const;
type WinnowImageProps = {
  src: string;
  alt: string;
  caption?: string;
  isVideo?: boolean;
};
function WinnowImage({
  src,
  alt,
  caption,
  isVideo
}: WinnowImageProps) {
  const reduceMotion = useReducedMotion();
  return <motion.figure initial={reduceMotion ? false : {
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.28,
    ease: [0.22, 1, 0.36, 1]
  }} className="my-10 md:my-14" data-id="element-387" data-mp="367kafc2b3v35">
      {isVideo ? <video src={src} aria-label={alt} className="block h-auto w-full" autoPlay loop muted playsInline preload="metadata" data-id="element-388" data-mp="1a4flq46kzswt" /> : <img src={src} alt={alt} className="block h-auto w-full" loading="lazy" decoding="async" data-id="element-389" data-mp="1o3yg7u6gz5qn" />}
      {caption && <figcaption className="mt-3 flex items-start gap-3 text-xs font-medium uppercase leading-relaxed tracking-[0.08em] text-muted" data-id="element-390" data-mp="1t28bs5w3w8cs">
          <span aria-hidden="true" className="mt-[0.45rem] h-px w-5 shrink-0 bg-cobalt" data-id="element-391" data-mp="1xu9t4684sp9o" />
          <span data-id="element-392" data-mp="21u1zcca1yjbv">{caption}</span>
        </figcaption>}
    </motion.figure>;
}
function CaseHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mt-20 text-3xl font-semibold tracking-[-0.03em] md:mt-28 md:text-5xl" data-id="element-393" data-mp="2bt9hrdvn945u">{children}</h2>;
}
function ProjectFact({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="min-w-0" data-id="element-394" data-mp="2gymtqq4vql39">
      <dt className="font-display text-xs font-medium uppercase tracking-[0.1em] text-muted" data-id="element-395" data-mp="2gxbo0krvj39i">{label}</dt>
      <dd className="mt-1.5 break-words text-sm leading-snug text-foreground md:text-base" data-id="element-396" data-mp="2c01rkl62hrc6">{value}</dd>
    </div>;
}

/** The legacy Winnow study preserved in its original narrative and media order. */
export function WinnowCaseStudy() {
  const reduceMotion = useReducedMotion();
  const project = getProjectBySlug('winnow');
  const next = getNextProject('winnow');
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant' as ScrollBehavior
    });
  }, []);
  return <article className="pb-24 pt-28 md:pb-32 md:pt-36" data-id="element-397" data-mp="2qxccsm8iibl3">
      <div className="site-container" data-id="element-398" data-mp="2qugbcnxgh4rw">
        <Link to="/#work" className="group inline-flex items-center gap-2 font-display text-base font-medium uppercase tracking-[0.1em] text-muted transition-colors duration-200 hover:text-cobalt focus-visible:outline-none focus-visible:text-cobalt" data-id="element-399" data-mp="3neyr4ozvnyxs">
          <ArrowLeftIcon size={16} aria-hidden="true" className="transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-400" data-mp="384io41aw4qtn" />
          Back to work
        </Link>
      </div>
      <header className="site-container pt-12 md:pt-16" data-id="element-401" data-mp="2y6mejdxlq3xn">
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-402" data-mp="2y7e2c2kyixdd">
          <SectionLabel data-id="element-403" data-mp="3qus18687u304">UI/UX</SectionLabel>
        </motion.div>
        <motion.h1 initial={reduceMotion ? false : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        delay: reduceMotion ? 0 : 0.04,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-7 text-4xl font-medium leading-[1.04] tracking-[-0.04em] md:text-6xl lg:text-7xl" data-id="element-404" data-mp="3d4ok1to70zhj">
          Winnow
        </motion.h1>
      </header>

      <motion.div initial={reduceMotion ? false : {
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      delay: reduceMotion ? 0 : 0.08,
      ease: [0.22, 1, 0.36, 1]
    }} className="site-container mt-8 md:mt-10" data-id="element-405" data-mp="1y6ahq3rpmp9l">
        <img src={WINNOW_IMAGES.cover} alt="Winnow product screens layered over a red background" className="block h-auto w-full" loading="eager" decoding="async" data-id="element-406" data-mp="22xith6giimpx" />
      </motion.div>

      {project && <motion.dl initial={reduceMotion ? false : {
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      delay: reduceMotion ? 0 : 0.12,
      ease: [0.22, 1, 0.36, 1]
    }} className="site-container mt-8 grid grid-cols-2 gap-x-6 gap-y-7 md:mt-10 md:grid-cols-4 md:gap-x-10" data-id="element-407" data-mp="1aaek6padzm8c">
          <ProjectFact label="Client" value={project.client} data-id="element-408" data-mp="2vmg9t415y9g1" />
          <ProjectFact label="Role" value={project.role} data-id="element-409" data-mp="2vlwp5fk4p6yb" />
          <ProjectFact label="Year" value={project.year} data-id="element-410" data-mp="32q63wrrdqhtz" />
          <ProjectFact label="Team" value="2 designers" data-id="element-411" data-mp="32rqyuihlw2sd" />
        </motion.dl>}

      <motion.section initial={reduceMotion ? false : {
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      delay: reduceMotion ? 0 : 0.16,
      ease: [0.22, 1, 0.36, 1]
    }} className="site-container mt-14 max-w-6xl md:mt-16" aria-labelledby="winnow-overview" data-id="element-412" data-mp="37qk9821ljdob">
        <SectionLabel data-id="element-413" data-mp="2oxq2fve4mrv6">Overview</SectionLabel>
        <p id="winnow-overview" className="mt-5 max-w-3xl text-left text-base leading-relaxed text-muted md:text-lg" data-id="element-414" data-mp="2oyhoaehx9b89">
          Winnow Digital Marketing delivers high-quality leads to clients through the use of their AI-assisted MarTech Platform. They capture these high-quality leads on a variety of digital advertising platforms including, Facebook, Instagram, Twitter, Amazon, LinkedIn &amp; Google.
        </p>
      </motion.section>

      <section className="site-container mt-14 max-w-6xl md:mt-16" aria-labelledby="winnow-team" data-id="element-415" data-mp="ncdng02ckxv0">
        <SectionLabel data-id="element-416" data-mp="jasg0u8l1bs7">My team</SectionLabel>
        <div className="mt-4" data-id="element-417" data-mp="jbtp4t61fo7a">
          <h2 id="winnow-team" className="text-xl font-semibold tracking-[-0.02em] md:text-2xl" data-id="element-418" data-mp="j8o2rlosn0f9">Chris Leyva</h2>
          <p className="mt-1 text-sm text-muted md:text-base" data-id="element-419" data-mp="edijyvqou1ve">Sr Product Designer @ deepwatch</p>
        </div>
      </section>

      <div className="site-container mt-20 max-w-6xl md:mt-28" data-id="element-420" data-mp="4e1dwd7ixlyw">
        <CaseHeading data-id="element-421" data-mp="4fm9m65krayc">My role</CaseHeading>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-422" data-mp="3vrxdyrbmw26d">
          Winnow had an existing product that their team was using. I was initially brought onto this project to improve the UI/UX of their internal platform (referred to as “Winnow Admin”) and elevate it from proof of concept to minimum viable product. After a few discovery sessions, we also decided to include a new feature; the campaign builder.
        </p>

        <CaseHeading data-id="element-423" data-mp="3cvecmo72nikd">Our Process</CaseHeading>
        <ol className="mt-7 list-decimal space-y-2 pl-5 text-base leading-relaxed text-muted md:text-lg" data-id="element-424" data-mp="15hl1g9vhsr5c">
          <li data-id="element-425" data-mp="19jzo752a8b1w">Discovery (show and tell)</li>
          <li data-id="element-426" data-mp="19hltwyfff2ve">Research (affinity mapping)</li>
          <li data-id="element-427" data-mp="vkgisk0uvdta">Planning (cognitive map; scope)</li>
          <li data-id="element-428" data-mp="vjorvmi7e49c">Wireframe (low-fidelity)</li>
          <li data-id="element-429" data-mp="10ii99q0m2fes">Prototype (high-fidelity)</li>
          <li data-id="element-430" data-mp="10hgp8w1maikx">Delivery (development)</li>
        </ol>

        <CaseHeading data-id="element-431" data-mp="ltltt7w86045">Discovery</CaseHeading>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-432" data-mp="lskdop0cqnfk">
          We started with high level overviews of company goals starting with the big picture and narrowing in. Examining all Winnow products (internal and client facing) to gauge how the internal app played a role among the larger scheme of things.
        </p>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-433" data-mp="2vj6qyn8md8z">
          First we asked Winnow to walk through their typical day-to-day tasks in the application on a team by team basis taking note of each teams flow and their objectives. We also asked to take note of any pain points for later. Then we performed a Usability Evaluation.
        </p>
        <WinnowImage src={WINNOW_IMAGES.originalApp} alt="The original Winnow Admin product" data-id="element-434" data-mp="3geps1gi8styj" />

        <CaseHeading data-id="element-435" data-mp="3ua1p3ujvewim">Research</CaseHeading>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-436" data-mp="3pcrzoewhekus">
          Now it was time to come back to the pain points and analyze them a bit further. Using the affinity mapping approach, we had each team member write out their wants on sticky notes. Either resolutions to pain points or completely new items they wanted to see. Then organized them by priority in order to determine where there was overlap and which issues and wants were highest on the list.
        </p>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-437" data-mp="qumg8wn8asz2">
          Granted our goal was not to introduce new features, many of the wants were determined to be tied to UX issues and the complexity of the UI.
        </p>
        <WinnowImage src={WINNOW_IMAGES.affinityMap} alt="Affinity mapping workshop for the Winnow product" caption="Affinity mapping" data-id="element-438" data-mp="3qe6h38qh11xn" />
        <p className="mt-10 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-439" data-mp="3glsdsbjqvn9d">
          The Information Architechture was quite convoluted which lead to a confusing navigation. We decided to perform a card-sorting exercise in order to gain a clear understanding of what the iA should be which would help improve overall navigation.
        </p>
        <WinnowImage src={WINNOW_IMAGES.cardSorting} alt="Card sorting exercise used to improve Winnow navigation" caption="The result of the card-sorting exercise was a simplified IA and navigation" data-id="element-440" data-mp="879yborh55fq" />

        <CaseHeading data-id="element-441" data-mp="3vawt9kz5ndds">Planning</CaseHeading>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-442" data-mp="2ovnl7pltztx2">
          First we decided we needed to creating a concept map to scope out all the pages and flows at a high level. The goal was to brainstorm and include improvements to overall structure of the application to present to the stakeholders.
        </p>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-443" data-mp="14dlejnsjr761">
          Granted our goal was not to introduce new features, many of the wants were determined to be tied to UX issues and the complexity of the UI.
        </p>
        <WinnowImage src={WINNOW_IMAGES.sitemap} alt="Whimsical concept map for the Winnow application" caption="Whimsical concept map" data-id="element-444" data-mp="1i4qiv36v0nmp" />

        <CaseHeading data-id="element-445" data-mp="1n29vxzrtmud2">Wireframes</CaseHeading>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-446" data-mp="1s2o7hrsmsmfu">
          Initial lo-fi mockups were used to scope each screen as well as experiment with new solutions (eg. navigation). It also provided a starting point for deeper client feedback. The Winnow team was great at providing feedback as we pushed iterations live for the team to see.
        </p>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-447" data-mp="1sb6gxvwvgnt">
          Once we got a good spot we had a final presentation of the wireframes and approval from the stakeholders to move into the high fidelity mockups.
        </p>
        <WinnowImage src={WINNOW_IMAGES.wireImprovements} alt="Initial Winnow wireframes and navigation improvements" caption="Initial wireframes and improvements to navigation as we pushed through iterations" data-id="element-448" data-mp="1e22kz69k9763" />
        <WinnowImage src={WINNOW_IMAGES.wireHighlights} alt="Animated Winnow wireframe highlights" caption="Wireframe highlights" isVideo data-id="element-449" data-mp="1nyp6lx9po296" />

        <CaseHeading data-id="element-450" data-mp="1iz2sllm26hzb">Highlight</CaseHeading>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-451" data-mp="1xq31frqpwdvz">
          The most notable achievement of working through the wireframes was improving the navigation. Many pain points expressed by the team seemed to circulate around the navigation - particularly in working with clients - which was the biggest area of use in the app.
        </p>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-452" data-mp="2ld6ufg837cgy">
          Previously when users were jumping to different pages of the app they needed to select the client they wanted to view every single time making it difficult to flow easily through the app. It was expressed that most often you’re working with one client at a time. So we changed the navigation to elevate the selection of the client as part of a top level item, highlighting which client was being worked on, and cascading the pages relative to a client underneath.
        </p>
        <WinnowImage src={WINNOW_IMAGES.clientFlow} alt="Winnow client workflow highlight" caption="Highlight of client workflow" data-id="element-453" data-mp="27fibidxkyfej" />
        <WinnowImage src={WINNOW_IMAGES.clientFlowExample} alt="Example of Winnow client navigation" caption="Client navigation example" data-id="element-454" data-mp="1xnckp7j2e40x" />

        <CaseHeading data-id="element-455" data-mp="1jp5umdkcx8bt">Prototypes</CaseHeading>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-456" data-mp="1omxxu3qgcgvb">
          The sexy hi-fi screens that became the deliverables. With the many steps of the process behind us, and the UX issues confidently resolved, this stage involved polishing the little details and giving a face lift to the UI.
        </p>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-457" data-mp="16vn6hice9c4w">
          After a final push we presented to the team and, after a couple of revisions, received a final seal of approval.
        </p>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-458" data-mp="xwjnsfh52yqx">
          Below are some gifs highlighting parts of the new client workflows:
        </p>
        <MediaCarousel label="High-fidelity workflow prototypes" items={[{
        src: WINNOW_IMAGES.hifiProfile,
        alt: 'Animated Winnow client profile prototype',
        caption: 'Client profile',
        isVideo: true
      }, {
        src: WINNOW_IMAGES.hifiPostCreation,
        alt: 'Animated Winnow client post creation prototype',
        caption: 'Client post creation',
        isVideo: true
      }, {
        src: WINNOW_IMAGES.hifiAnalytics,
        alt: 'Animated Winnow client analytics prototype',
        caption: 'Client analytics',
        isVideo: true
      }]} data-id="element-459" data-mp="nwsugh4bepsf" />

        <CaseHeading data-id="element-460" data-mp="3d8r5t6gqkfmr">Delivery</CaseHeading>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted md:text-lg" data-id="element-461" data-mp="3d9ssk8746qkb">
          The Winnow team previously only received static mockups (😱). So having taught them about InVision - which we were using throughout the process for feedback - they were very pleased to learn the advantages of modern technology. They were pleased with simply access to the InVision project and took the designs and ran with them.
        </p>
      </div>

      <div className="site-container mt-24 md:mt-32" data-id="element-462" data-mp="11lkqp4oiuqc3">
        <SectionLabel data-id="element-463" data-mp="16h03ruepgi7g">Next case study</SectionLabel>
        <Link to={`/work/${next.slug}`} className="group mt-10 flex items-end justify-between gap-6 border-b border-border pb-8 focus-visible:outline-none" data-id="element-464" data-mp="32lfw923vls4w">
          <div className="min-w-0" data-id="element-465" data-mp="2ym7421ohi2bg">
            <h2 className="break-words text-4xl font-medium leading-[1.03] tracking-[-0.035em] transition-colors duration-200 group-hover:text-cobalt md:text-6xl" data-id="element-466" data-mp="3cjvqvf243jj5">
              {next.title}
            </h2>
            <p className="mt-3 text-muted" data-id="element-467" data-mp="2jvzwih00m3pj">{next.client}</p>
          </div>
          <ArrowUpRightIcon size={34} aria-hidden="true" className="mb-1 shrink-0 transition-transform duration-200 ease-out group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-cobalt" data-id="element-468" data-mp="2ew56efo2di5j" />
        </Link>
      </div>
    </article>;
}